import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, Area, AreaChart, Legend
} from 'recharts';
import SectionPdfExport, { buildColumns } from '@/components/SectionPdfExport';

const fmt = (v) => {
  if (v === undefined || v === null || isNaN(v)) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v);
};
const pct = (v) => v !== undefined && v !== null ? `${(v * 100).toFixed(1)}%` : '—';

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-zinc-900/95 backdrop-blur-sm border border-zinc-800 p-3 rounded-lg shadow-xl">
      <p className="text-xs text-zinc-400 mb-1 font-mono">{label}</p>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2 text-xs">
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
          <span className="text-zinc-400">{p.name}:</span>
          <span className="font-mono text-zinc-200 font-medium">
            {typeof p.value === 'number' && Math.abs(p.value) > 1 ? fmt(p.value) : pct(p.value)}
          </span>
        </div>
      ))}
    </div>
  );
};

export default function PnLSummaryPage({ results, analysis }) {
  const pnl = results?.historical_pnl || [];

  if (pnl.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[40vh] text-zinc-500 text-sm" data-testid="pnl-page">
        Import data to view P&L summary
      </div>
    );
  }

  const chartData = pnl.map(p => ({
    year: p.year,
    Revenue: p.revenue,
    COGS: Math.abs(p.cogs),
    'Gross Profit': p.gross_profit,
    'Op. Income': p.operating_income,
  }));

  const marginData = pnl.map(p => ({
    year: p.year,
    'Gross Margin': p.gross_margin,
    'Op. Margin': p.operating_margin,
    'Net Margin': p.net_margin,
  }));

  return (
    <div className="space-y-6 animate-fade-in" data-testid="pnl-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-zinc-100 font-['Manrope']">Historical P&L & Operating Margins</h2>
          <p className="text-sm text-zinc-400 mt-1">Multi-year revenue, costs, and margin trends</p>
        </div>
        <SectionPdfExport
          title="P&L Summary"
          analysisName={analysis?.name}
          rows={pnl}
          columns={buildColumns([
            { label: 'Year', key: 'year' },
            { label: 'Revenue', key: 'revenue', format: 'currency' },
            { label: 'Gross Profit', key: 'gross_profit', format: 'currency' },
            { label: 'GM%', key: 'gross_margin', format: 'percent' },
            { label: 'Op. Income', key: 'operating_income', format: 'currency' },
            { label: 'Op. Margin', key: 'operating_margin', format: 'percent' },
          ])}
        />
      </div>

      {/* Revenue Chart */}
      <Card className="bg-zinc-900/60 border-zinc-800/60">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-zinc-300">Revenue & Gross Profit</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={chartData} barGap={2}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
              <XAxis dataKey="year" tick={{ fill: '#71717a', fontSize: 11 }} />
              <YAxis tick={{ fill: '#71717a', fontSize: 11 }} tickFormatter={(v) => `$${(v / 1000000).toFixed(1)}M`} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 11, color: '#a1a1aa' }} />
              <Bar dataKey="Revenue" fill="#6366f1" radius={[3, 3, 0, 0]} />
              <Bar dataKey="Gross Profit" fill="#10b981" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Margin Trends */}
      <Card className="bg-zinc-900/60 border-zinc-800/60">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-zinc-300">Margin Trends</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={marginData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
              <XAxis dataKey="year" tick={{ fill: '#71717a', fontSize: 11 }} />
              <YAxis tick={{ fill: '#71717a', fontSize: 11 }} tickFormatter={(v) => `${(v * 100).toFixed(0)}%`} domain={['auto', 'auto']} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 11, color: '#a1a1aa' }} />
              <Line type="monotone" dataKey="Gross Margin" stroke="#6366f1" strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="Op. Margin" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="Net Margin" stroke="#ef4444" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Data Table */}
      <Card className="bg-zinc-900/60 border-zinc-800/60 overflow-hidden">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-zinc-300">Detailed P&L Table</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-800/60 hover:bg-transparent">
                <TableHead className="text-zinc-400 text-xs">Year</TableHead>
                <TableHead className="text-zinc-400 text-xs text-right">Revenue</TableHead>
                <TableHead className="text-zinc-400 text-xs text-right">COGS</TableHead>
                <TableHead className="text-zinc-400 text-xs text-right">Gross Profit</TableHead>
                <TableHead className="text-zinc-400 text-xs text-right">GM %</TableHead>
                <TableHead className="text-zinc-400 text-xs text-right">Op. Income</TableHead>
                <TableHead className="text-zinc-400 text-xs text-right">Op. Margin</TableHead>
                <TableHead className="text-zinc-400 text-xs text-right">YoY Growth</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pnl.map((row) => (
                <TableRow key={row.year} className="border-zinc-800/40 hover:bg-zinc-800/20">
                  <TableCell className="font-mono text-xs text-zinc-200 font-medium">{row.year}</TableCell>
                  <TableCell className="text-right font-mono text-xs text-zinc-200">{fmt(row.revenue)}</TableCell>
                  <TableCell className="text-right font-mono text-xs text-red-400">{fmt(row.cogs)}</TableCell>
                  <TableCell className="text-right font-mono text-xs text-zinc-200">{fmt(row.gross_profit)}</TableCell>
                  <TableCell className="text-right font-mono text-xs text-zinc-300">{pct(row.gross_margin)}</TableCell>
                  <TableCell className={`text-right font-mono text-xs ${row.operating_income >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {fmt(row.operating_income)}
                  </TableCell>
                  <TableCell className={`text-right font-mono text-xs ${row.operating_margin >= 0 ? 'text-zinc-300' : 'text-red-400'}`}>
                    {pct(row.operating_margin)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs">
                    {row.yoy_growth !== null ? (
                      <Badge variant="outline" className={`text-[10px] ${row.yoy_growth >= 0 ? 'border-emerald-500/30 text-emerald-400' : 'border-red-500/30 text-red-400'}`}>
                        {row.yoy_growth >= 0 ? '+' : ''}{pct(row.yoy_growth)}
                      </Badge>
                    ) : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
